import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import Modal from '../../components/Modal';
import MultiSelectBusca from '../../components/MultiSelectBusca';
import api from '../../api';

// O tipo decide permissão e quem entra no Minha Equipe — por isso cada um explica o efeito.
const TIPOS = [
  { v: 'ceo', l: 'CEO', efeito: 'Acompanha o progresso de todos e cria atas.', protegido: true },
  { v: 'diretoria', l: 'Diretoria', efeito: 'Acompanha o progresso de todos e cria atas.', protegido: true },
  { v: 'supervisao', l: 'Supervisão', efeito: 'Acompanha o progresso de todos.', protegido: true },
  { v: 'gerencia', l: 'Gerência', efeito: 'Tem "Minha Equipe" com quem está abaixo dela no organograma.' },
  { v: 'operacional', l: 'Operacional', efeito: 'Equipe de loja/depósito: entra no "Minha Equipe" do gerente da filial dela.' },
  { v: 'auxiliar', l: 'Auxiliar', efeito: 'Aparece no organograma, mas não entra no "Minha Equipe" do gerente da loja.' },
];
const tipoDe = (v) => TIPOS.find(t => t.v === v) || { l: v, efeito: '' };
const toast = (title) => Swal.fire({ toast: true, position: 'bottom-end', icon: 'success', title, timer: 2800, showConfirmButton: false });

// Achata a árvore (pai → filhos) guardando a profundidade, pra tabela com recuo e pros selects.
function achatar(posicoes) {
  const filhos = {};
  posicoes.forEach(p => { (filhos[p.parent_id || 'raiz'] = filhos[p.parent_id || 'raiz'] || []).push(p); });
  const ids = new Set(posicoes.map(p => p.id));
  const raizes = posicoes.filter(p => !p.parent_id || !ids.has(p.parent_id));
  const out = [];
  const visita = (p, d) => { out.push({ ...p, nivel: d }); (filhos[p.id] || []).forEach(f => visita(f, d + 1)); };
  raizes.forEach(r => visita(r, 0));
  return out;
}

export default function Posicoes() {
  const [posicoes, setPosicoes] = useState([]);
  const [podeAdmin, setPodeAdmin] = useState(false);
  const [manuais, setManuais] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState(null);
  const [manual, setManual] = useState(null);
  const [salvando, setSalvando] = useState(false);

  function carregar() {
    return Promise.all([api.get('/posicoes'), api.get('/posicoes/manuais')])
      .then(([p, m]) => {
        setPosicoes(p.data.posicoes); setPodeAdmin(!!p.data.pode_admin);
        setManuais(m.data.manuais); setPessoas(m.data.pessoas);
      })
      .catch(err => Swal.fire('Erro', err.response?.data?.error || 'Não foi possível carregar.', 'error'))
      .finally(() => setCarregando(false));
  }
  useEffect(() => { carregar(); }, []);

  const arvore = useMemo(() => achatar(posicoes), [posicoes]);
  const opcoesPosicao = arvore.map(p => ({ id: p.id, label: `${'— '.repeat(p.nivel)}${p.titulo}` }));
  const pessoasOptions = pessoas.map(p => ({ value: p.id, label: p.nome, hint: p.cargo || '' }));
  const travadoTipo = (t) => !podeAdmin && tipoDe(t).protegido;

  async function salvarPosicao() {
    if (!form.titulo.trim()) return Swal.fire('Falta o nome', 'Dê um nome à posição.', 'warning');
    if (!form.raiz && !form.parent_id) return Swal.fire('Falta o lugar', 'Escolha debaixo de qual posição ela fica.', 'warning');
    setSalvando(true);
    const payload = { titulo: form.titulo.trim(), tipo: form.tipo, ...(form.raiz ? {} : { parent_id: form.parent_id }) };
    if (String(form.ordem).trim() !== '') payload.ordem = parseInt(form.ordem) || 0;
    try {
      if (form.id) await api.put(`/posicoes/${form.id}`, payload);
      else await api.post('/posicoes', payload);
      toast(form.id ? 'Posição atualizada' : 'Posição criada');
      setForm(null);
      carregar();
    } catch (err) {
      Swal.fire('Não deu', err.response?.data?.error || 'Não foi possível salvar.', 'error');
    } finally { setSalvando(false); }
  }

  async function excluir(p) {
    const r = await Swal.fire({
      icon: 'warning', title: `Excluir "${p.titulo}"?`, text: 'A posição sai do organograma.',
      showCancelButton: true, confirmButtonText: 'Sim, excluir', cancelButtonText: 'Cancelar', confirmButtonColor: '#C62828',
    });
    if (!r.isConfirmed) return;
    try { await api.delete(`/posicoes/${p.id}`); toast('Posição excluída'); carregar(); }
    catch (err) { Swal.fire('Não deu', err.response?.data?.error || 'Não foi possível excluir.', 'error'); }
  }

  async function salvarManual() {
    if (!manual.funcionario_id) return Swal.fire('Falta a pessoa', 'Escolha quem vai ter a posição definida à mão.', 'warning');
    if (!manual.perfil_id) return Swal.fire('Falta a posição', 'Escolha onde a pessoa fica no organograma.', 'warning');
    setSalvando(true);
    try {
      await api.put('/posicoes/manuais', { funcionario_id: manual.funcionario_id, perfil_id: manual.perfil_id, observacao: manual.observacao || null });
      toast('Posição definida. O organograma já mudou; o acesso muda no próximo login.');
      setManual(null);
      carregar();
    } catch (err) {
      Swal.fire('Não deu', err.response?.data?.error || 'Não foi possível salvar.', 'error');
    } finally { setSalvando(false); }
  }

  async function voltarProCargo(m) {
    const r = await Swal.fire({
      icon: 'question', title: `${m.nome} volta pro cargo?`,
      text: `Ela deixa de ficar em "${m.posicao}" e passa a seguir o cargo do RH (${m.cargo || 'sem cargo'}).`,
      showCancelButton: true, confirmButtonText: 'Sim, voltar pro cargo', cancelButtonText: 'Cancelar',
    });
    if (!r.isConfirmed) return;
    try { await api.delete(`/posicoes/manuais/${m.funcionario_id}`); toast('Pronto'); carregar(); }
    catch (err) { Swal.fire('Não deu', err.response?.data?.error || 'Não foi possível.', 'error'); }
  }

  return (
    <>
      <Header />
      <main className="conteudo-container" style={{ maxWidth: 1040 }}>
        <Link to="/admin" className="doc-voltar"><i className="bi bi-arrow-left"></i> Administração</Link>
        <div className="adm-topo">
          <div>
            <h1>Organograma</h1>
            <p>
              As posições do organograma e quem fica em cada uma. Cada pessoa fica na posição do <strong>cargo dela</strong>
              (tela <Link className="adm-link" to="/admin/cargos">Cargos</Link>) — a não ser que tenha uma posição <strong>definida à mão</strong> aqui embaixo.
            </p>
          </div>
          <button className="btn-centrao" onClick={() => setForm({ id: null, titulo: '', tipo: 'operacional', parent_id: '', ordem: '', raiz: false })}>
            <i className="bi bi-plus-lg me-1"></i> Nova posição
          </button>
        </div>

        {carregando ? <div>Carregando…</div> : (
          <>
            <div className="adm-tabela-wrap">
              <table className="adm-tabela">
                <thead><tr><th>Posição</th><th>Tipo</th><th className="adm-num">Pessoas</th><th></th></tr></thead>
                <tbody>
                  {arvore.map(p => {
                    const total = (p.pessoas_pelo_cargo || 0) + (p.pessoas_a_mao || 0);
                    const trava = travadoTipo(p.tipo);
                    return (
                      <tr key={p.id}>
                        <td style={{ paddingLeft: 12 + p.nivel * 22 }}>
                          {p.nivel > 0 && <span className="adm-sub" style={{ marginRight: 6 }}>└</span>}
                          <strong>{p.titulo}</strong>
                          {p.so_no_portal && <> <span className="adm-badge adm-badge--neutro" title="Nenhum cargo do RH aponta pra ela — quem está aqui foi posto à mão">só no portal</span></>}
                          {p.protegida && <> <span className="adm-badge adm-badge--neutro" title="O sistema usa essa posição nas regras do Minha Equipe">usada pelo sistema</span></>}
                        </td>
                        <td><span className="adm-badge adm-badge--ok" title={tipoDe(p.tipo).efeito}>{tipoDe(p.tipo).l}</span></td>
                        <td className="adm-num">
                          {total || <span className="adm-sub">—</span>}
                          {p.pessoas_a_mao > 0 && <div className="adm-sub">{p.pessoas_a_mao} à mão</div>}
                        </td>
                        <td>
                          <div className="adm-acoes">
                            <button className="adm-btn-icone" title="Nova posição abaixo desta"
                                    onClick={() => setForm({ id: null, titulo: '', tipo: 'operacional', parent_id: p.id, ordem: '', raiz: false })}>
                              <i className="bi bi-plus-lg"></i>
                            </button>
                            <button className="adm-btn-icone" title={trava ? 'Só administrador mexe nessa posição' : 'Editar'} disabled={trava}
                                    onClick={() => setForm({ id: p.id, titulo: p.titulo, tipo: p.tipo, parent_id: p.parent_id || '', ordem: p.ordem ?? '', raiz: !p.parent_id })}>
                              <i className="bi bi-pencil"></i>
                            </button>
                            {!p.protegida && p.parent_id && (
                              <button className="adm-btn-icone adm-btn-icone--perigo" title="Excluir" disabled={trava} onClick={() => excluir(p)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="adm-topo" style={{ marginTop: 28 }}>
              <div>
                <h2 className="trilha-secao" style={{ margin: 0 }}>Pessoas com posição definida à mão</h2>
                <p className="adm-dica" style={{ margin: '4px 0 0' }}>
                  Pra quando o cargo do RH não diz onde a pessoa realmente fica (ex.: sócio que é diretor, gerente que atua como supervisor).
                </p>
              </div>
              <button className="btn btn-outline-secondary" onClick={() => setManual({ funcionario_id: '', perfil_id: '', observacao: '' })}>
                <i className="bi bi-pin-map me-1"></i> Definir posição de alguém
              </button>
            </div>
            <div className="adm-tabela-wrap">
              <table className="adm-tabela">
                <thead><tr><th>Pessoa</th><th>Cargo no RH</th><th>Fica em</th><th>Motivo</th><th></th></tr></thead>
                <tbody>
                  {manuais.map(m => {
                    const trava = travadoTipo(m.tipo);
                    return (
                      <tr key={m.funcionario_id}>
                        <td><strong>{m.nome}</strong></td>
                        <td>{m.cargo || <span className="adm-sub">—</span>}</td>
                        <td>{m.posicao}</td>
                        <td className="adm-sub">{m.observacao || '—'}</td>
                        <td>
                          <div className="adm-acoes">
                            <button className="adm-btn-icone" title={trava ? 'Só administrador' : 'Trocar posição'} disabled={trava}
                                    onClick={() => setManual({ funcionario_id: m.funcionario_id, perfil_id: m.perfil_id, observacao: m.observacao || '', travarPessoa: true })}>
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="adm-btn-icone adm-btn-icone--perigo" title={trava ? 'Só administrador' : 'Voltar pro cargo do RH'} disabled={trava}
                                    onClick={() => voltarProCargo(m)}>
                              <i className="bi bi-arrow-counterclockwise"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {manuais.length === 0 && <tr><td colSpan={5} className="adm-vazio">Ninguém com posição à mão.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      <Modal
        aberto={!!form}
        titulo={form?.id ? 'Editar posição' : 'Nova posição'}
        largura={560}
        onFechar={() => setForm(null)}
        rodape={<>
          <button className="btn btn-outline-secondary" onClick={() => setForm(null)}>Cancelar</button>
          <button className="btn-centrao" disabled={salvando} onClick={salvarPosicao}>{salvando ? 'Salvando…' : 'Salvar posição'}</button>
        </>}
      >
        {form && (
          <>
            <label className="doc-label">Nome da posição *</label>
            <input className="doc-input" value={form.titulo} maxLength={120} autoFocus placeholder="Ex.: Auxiliar Administrativo"
                   onChange={e => setForm(s => ({ ...s, titulo: e.target.value }))} />
            {!form.raiz && (
              <>
                <label className="doc-label">Fica debaixo de *</label>
                <select className="doc-input" value={form.parent_id} onChange={e => setForm(s => ({ ...s, parent_id: e.target.value }))}>
                  <option value="">Escolha…</option>
                  {opcoesPosicao.filter(o => o.id !== form.id).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </>
            )}
            <label className="doc-label">Tipo *</label>
            <select className="doc-input" value={form.tipo} onChange={e => setForm(s => ({ ...s, tipo: e.target.value }))}>
              {TIPOS.map(t => (
                <option key={t.v} value={t.v} disabled={!podeAdmin && t.protegido}>
                  {t.l}{!podeAdmin && t.protegido ? ' (só administrador)' : ''}
                </option>
              ))}
            </select>
            <p className="doc-hint" style={{ marginTop: 6 }}><i className="bi bi-info-circle me-1"></i>{tipoDe(form.tipo).efeito}</p>
            <label className="doc-label">Ordem</label>
            <input className="doc-input" type="number" value={form.ordem} placeholder="Em branco = vai pro fim"
                   onChange={e => setForm(s => ({ ...s, ordem: e.target.value }))} />
          </>
        )}
      </Modal>

      <Modal
        aberto={!!manual}
        titulo="Definir posição à mão"
        largura={560}
        onFechar={() => setManual(null)}
        rodape={<>
          <button className="btn btn-outline-secondary" onClick={() => setManual(null)}>Cancelar</button>
          <button className="btn-centrao" disabled={salvando} onClick={salvarManual}>{salvando ? 'Salvando…' : 'Salvar'}</button>
        </>}
      >
        {manual && (
          <>
            <label className="doc-label">Pessoa *</label>
            {manual.travarPessoa
              ? <div className="doc-input" style={{ background: '#f6f6f6' }}>{pessoas.find(p => p.id === manual.funcionario_id)?.nome}</div>
              : <MultiSelectBusca
                  options={pessoasOptions}
                  values={manual.funcionario_id ? [manual.funcionario_id] : []}
                  onChange={vals => setManual(s => ({ ...s, funcionario_id: vals.length ? vals[vals.length - 1] : '' }))}
                  placeholder="Buscar pessoa…" icon="bi-person" />}
            <label className="doc-label">Fica em *</label>
            <select className="doc-input" value={manual.perfil_id} onChange={e => setManual(s => ({ ...s, perfil_id: e.target.value }))}>
              <option value="">Escolha…</option>
              {arvore.map(p => (
                <option key={p.id} value={p.id} disabled={travadoTipo(p.tipo)}>
                  {`${'— '.repeat(p.nivel)}${p.titulo}`}{travadoTipo(p.tipo) ? ' (só administrador)' : ''}
                </option>
              ))}
            </select>
            <label className="doc-label">Motivo</label>
            <input className="doc-input" value={manual.observacao} maxLength={300} placeholder="Ex.: sócio que responde pela diretoria"
                   onChange={e => setManual(s => ({ ...s, observacao: e.target.value }))} />
          </>
        )}
      </Modal>
    </>
  );
}
